/**
 * useLocalAdmin Hook
 *
 * Wraps all local-admin:* IPC calls for the Electron admin dashboard.
 * Follows the same pattern as useArchiveApi, useTransformApi, etc.
 *
 * @module hooks/useLocalAdmin
 */

import { useCallback } from 'react';

const isElectron = typeof window !== 'undefined' && !!window.isElectron;

// ═══════════════════════════════════════════════════════════════════
// USAGE TRACKING
// ═══════════════════════════════════════════════════════════════════

export function useLocalAdmin() {
  const getUsageSummary = useCallback(async (billingPeriod?: string) => {
    if (!isElectron || !window.electronLocalAdmin) return null;
    return window.electronLocalAdmin.getUsageSummary(billingPeriod);
  }, []);

  const getUsageHistory = useCallback(async (periods?: number) => {
    if (!isElectron || !window.electronLocalAdmin) return [];
    return window.electronLocalAdmin.getUsageHistory(periods);
  }, []);

  const getRecentCalls = useCallback(async (limit?: number) => {
    if (!isElectron || !window.electronLocalAdmin) return [];
    return window.electronLocalAdmin.getRecentCalls(limit);
  }, []);

  const getUsageByModel = useCallback(async (billingPeriod?: string) => {
    if (!isElectron || !window.electronLocalAdmin) return [];
    return window.electronLocalAdmin.getUsageByModel(billingPeriod);
  }, []);

  const getUsageByBackend = useCallback(async (billingPeriod?: string) => {
    if (!isElectron || !window.electronLocalAdmin) return [];
    return window.electronLocalAdmin.getUsageByBackend(billingPeriod);
  }, []);

  // ═══════════════════════════════════════════════════════════════════
  // PROMPT OVERRIDES
  // ═══════════════════════════════════════════════════════════════════

  const listPrompts = useCallback(async () => {
    if (!isElectron || !window.electronLocalAdmin) return [];
    return window.electronLocalAdmin.listPrompts();
  }, []);

  const getPrompt = useCallback(async (promptId: string) => {
    if (!isElectron || !window.electronLocalAdmin) return null;
    return window.electronLocalAdmin.getPrompt(promptId);
  }, []);

  const updatePromptOverride = useCallback(async (
    promptId: string,
    template: string,
    description?: string
  ) => {
    if (!isElectron || !window.electronLocalAdmin) return { error: 'Not available' };
    return window.electronLocalAdmin.updatePromptOverride(promptId, template, description);
  }, []);

  const deletePromptOverride = useCallback(async (promptId: string) => {
    if (!isElectron || !window.electronLocalAdmin) return { error: 'Not available' };
    return window.electronLocalAdmin.deletePromptOverride(promptId);
  }, []);

  const resetAllPromptOverrides = useCallback(async () => {
    if (!isElectron || !window.electronLocalAdmin) return { error: 'Not available' };
    return window.electronLocalAdmin.resetAllPromptOverrides();
  }, []);

  const previewPrompt = useCallback(async (
    promptId: string,
    variables?: Record<string, string>
  ) => {
    if (!isElectron || !window.electronLocalAdmin) return null;
    return window.electronLocalAdmin.previewPrompt(promptId, variables);
  }, []);

  // ═══════════════════════════════════════════════════════════════════
  // CLOUD QUOTA CACHE
  // ═══════════════════════════════════════════════════════════════════

  const syncCloudQuota = useCallback(async () => {
    if (!isElectron || !window.electronLocalAdmin) return null;
    return window.electronLocalAdmin.syncCloudQuota();
  }, []);

  const getCachedQuota = useCallback(async () => {
    if (!isElectron || !window.electronLocalAdmin) return null;
    return window.electronLocalAdmin.getCachedQuota();
  }, []);

  // ═══════════════════════════════════════════════════════════════════
  // BYOK COST ALERTS
  // ═══════════════════════════════════════════════════════════════════

  const getByokAlerts = useCallback(async () => {
    if (!isElectron || !window.electronLocalAdmin) return null;
    return window.electronLocalAdmin.getByokAlerts();
  }, []);

  const setByokAlerts = useCallback(async (alerts: any) => {
    if (!isElectron || !window.electronLocalAdmin) return { error: 'Not available' };
    return window.electronLocalAdmin.setByokAlerts(alerts);
  }, []);

  const checkByokSpending = useCallback(async () => {
    if (!isElectron || !window.electronLocalAdmin) return null;
    return window.electronLocalAdmin.checkByokSpending();
  }, []);

  const exportUsageCSV = useCallback(async (billingPeriod?: string) => {
    if (!isElectron || !window.electronLocalAdmin) return null;
    return window.electronLocalAdmin.exportUsageCSV(billingPeriod);
  }, []);

  const getSystemStatus = useCallback(async () => {
    if (!isElectron || !window.electronLocalAdmin) return null;
    return window.electronLocalAdmin.getSystemStatus();
  }, []);

  // ═══════════════════════════════════════════════════════════════════
  // GPTZERO CONFIG
  // ═══════════════════════════════════════════════════════════════════

  const getGptzeroConfig = useCallback(async () => {
    if (!isElectron || !window.electronLocalAdmin) return null;
    return window.electronLocalAdmin.getGptzeroConfig();
  }, []);

  const setGptzeroConfig = useCallback(async (update: { monthlyWordLimit?: number; enabled?: boolean; proxyEnabled?: boolean }) => {
    if (!isElectron || !window.electronLocalAdmin) return null;
    return window.electronLocalAdmin.setGptzeroConfig(update);
  }, []);

  return {
    // Usage
    getUsageSummary,
    getUsageHistory,
    getRecentCalls,
    getUsageByModel,
    getUsageByBackend,
    // Prompts
    listPrompts,
    getPrompt,
    updatePromptOverride,
    deletePromptOverride,
    resetAllPromptOverrides,
    previewPrompt,
    // Quota
    syncCloudQuota,
    getCachedQuota,
    // BYOK Alerts
    getByokAlerts,
    setByokAlerts,
    checkByokSpending,
    // CSV export
    exportUsageCSV,
    // System status
    getSystemStatus,
    // GPTZero config
    getGptzeroConfig,
    setGptzeroConfig,
  };
}
